import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ANALYZE_STANDARD_IMPORT_MUTATION,
  ANALYZE_SMART_IMPORT_MUTATION,
  COMMIT_STANDARD_IMPORT_MUTATION,
  COMMIT_SMART_IMPORT_MUTATION,
  CONFIRM_SMART_IMPORT_MUTATION,
  DELETE_STANDARD_IMPORT_MUTATION,
  DELETE_SMART_IMPORT_MUTATION,
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_SITES_QUERY,
  reconcileFindsInCache,
  STANDARD_IMPORT_QUERY,
  SMART_IMPORT_QUERY,
  UPDATE_STANDARD_IMPORT_DRAFT_MUTATION,
  UPDATE_SMART_IMPORT_DRAFT_MUTATION,
} from '../../../gql';
import { formatDateTime } from '../../../helpers';
import {
  SiteChoice,
  SiteOption,
  SmartImportDraftFind,
  SmartImportDraftRow,
  SmartImportRecord,
  StandardImportDraftSession,
} from '../types';
import { isImportCancellable } from '../importStatus';
import { ImportDropzone } from './ImportDropzone';
import { ImportPreviewTable } from './ImportPreviewTable';
import { ImportProcessing } from './ImportProcessing';
import { ImportReviewTable } from './ImportReviewTable';
import { ImportWorkflowShell } from './ImportWorkflowShell';
import { NONE_SITE_CHOICE, SiteSelect } from './SiteSelect';
import { buildDraftInput } from './buildDraftInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatImportFailure, formatImportWarning } from '../importMessages';
import { SwitchLabel, Switch } from '../../../components/design-system';

const uploadMainStyles = { flexGrow: 1, minHeight: 0 };
const validationControlsStyles = {
  alignItems: { xs: 'stretch', md: 'center' },
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
  mb: 2,
};
const globalSiteStyles = { minWidth: { xs: '100%', md: 320 } };
const reviewToolbarStyles = {
  alignItems: { xs: 'stretch', md: 'center' },
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 1,
  mb: 2,
};
const reviewSiteStyles = { minWidth: { xs: '100%', md: 320 } };
const centeredPaperStyles = {
  alignSelf: 'center',
  maxWidth: 760,
  p: { xs: 2, md: 4 },
  width: '100%',
};
const successStepIconStyles = { color: 'success.main', fontSize: 20 };
const errorPaperStyles = { ...centeredPaperStyles, borderColor: 'error.main' };
const storingProgressStyles = { mt: 2 };
const skeletonRows = Array.from({ length: 9 }, (_, index) => index);
const smallBatchSuggestionStyles = {
  alignItems: 'center',
  color: 'text.secondary',
};

const DraftRowsSkeleton = React.memo(() => {
  const intl = useIntl();

  return (
    <Stack
      gap={1}
      aria-label={intl.formatMessage({
        id: 'imports.processing.loadingRows',
        defaultMessage: 'Loading import rows',
      })}
    >
      {skeletonRows.map((row) => (
        <Skeleton height={44} key={row} variant="rounded" />
      ))}
    </Stack>
  );
});

function getSiteChoice(siteId: string | null, sites: SiteOption[]): SiteChoice {
  if (siteId == null) {
    return NONE_SITE_CHOICE;
  }

  return (
    sites.find((site) => site.id === siteId) || {
      id: siteId,
      name: siteId,
    }
  );
}

function countDelimitedRows(source: string): number {
  let rowCount = 0;
  let hasContent = false;
  let quoted = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (character === '"') {
      if (quoted && source[index + 1] === '"') {
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && (character === '\n' || character === '\r')) {
      if (hasContent) {
        rowCount += 1;
        hasContent = false;
      }

      if (character === '\r' && source[index + 1] === '\n') {
        index += 1;
      }
    } else if (!/\s/.test(character)) {
      hasContent = true;
    }
  }

  return Math.max(0, rowCount + (hasContent ? 1 : 0) - 1);
}

async function countFinds(file: File): Promise<number | null> {
  const filename = file.name.toLocaleLowerCase();

  if (!/\.(gpx|kml|csv|tsv)$/.test(filename)) {
    return null;
  }

  const source = await file.text();

  if (filename.endsWith('.gpx')) {
    return source.match(/<(?:[\w.-]+:)?wpt\b/gi)?.length || 0;
  }

  if (filename.endsWith('.kml')) {
    const placemarks = source.match(
      /<(?:[\w.-]+:)?Placemark\b[\s\S]*?<\/(?:[\w.-]+:)?Placemark\s*>/gi
    );

    return (
      placemarks?.filter((placemark) =>
        /<(?:[\w.-]+:)?Point\b/i.test(placemark)
      ).length || 0
    );
  }

  return countDelimitedRows(source);
}

interface SessionRowProps {
  disabled: boolean;
  onIncludeChange: (sessionRef: string, include: boolean) => void;
  session: StandardImportDraftSession;
}

const sessionPaperStyles = { p: 1.5 };

const SessionRow = React.memo(
  ({ disabled, onIncludeChange, session }: SessionRowProps) => {
    const intl = useIntl();
    const handleChange = useCallback(
      (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        onIncludeChange(session.sessionRef, checked);
      },
      [onIncludeChange, session.sessionRef]
    );
    const timeRange = session.startedAt
      ? `${formatDateTime(session.startedAt)} – ${formatDateTime(
          session.endedAt
        )}`
      : intl.formatMessage({
          id: 'imports.session.noTimestamps',
          defaultMessage: 'No point timestamps; session cannot be imported',
        });

    return (
      <Paper variant="outlined" sx={sessionPaperStyles}>
        <FormControlLabel
          control={
            <Checkbox
              checked={session.include}
              disabled={disabled || session.startedAt == null}
              onChange={handleChange}
            />
          }
          label={
            session.name ||
            intl.formatMessage({
              id: 'imports.session.unnamed',
              defaultMessage: 'Unnamed GPX track or route',
            })
          }
        />
        <Typography color="text.secondary" variant="body2">
          <FormattedMessage
            id="imports.session.summary"
            defaultMessage="{timeRange} · {distance} km · {count, plural, one {# linked Find} other {# linked Finds}}"
            values={{
              timeRange,
              distance: (session.distanceMeters / 1000).toFixed(2),
              count: session.findRowRefs.length,
            }}
          />
        </Typography>
        {session.warnings.map((item) => (
          <Typography color="warning.main" key={item.code} variant="body2">
            {formatImportWarning(item, intl)}
          </Typography>
        ))}
      </Paper>
    );
  }
);

interface UploadImportProps {
  onClose: () => void;
}

const UploadImport = React.memo(({ onClose }: UploadImportProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [allowAi, setAllowAi] = useState(true);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFindCount, setSelectedFindCount] = useState<number | null>(
    null
  );
  const [analyze, { error: smartError, loading: smartLoading }] = useMutation(
    ANALYZE_SMART_IMPORT_MUTATION
  );
  const [
    analyzeStandard,
    { error: standardError, loading: standardLoading },
  ] = useMutation(ANALYZE_STANDARD_IMPORT_MUTATION);
  const error = smartError || standardError;
  const loading = smartLoading || standardLoading;

  const handleFileError = useCallback((message: string) => {
    setFileError(message);
    setSelectedFile(null);
    setSelectedFindCount(null);
  }, []);
  const handleAllowAiChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setAllowAi(checked);
    },
    []
  );
  const handleFile = useCallback(async (file: File) => {
    setFileError(null);
    setSelectedFile(file);
    setSelectedFindCount(null);

    try {
      setSelectedFindCount(await countFinds(file));
    } catch {
      setSelectedFindCount(null);
    }
  }, []);
  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    setFileError(null);

    try {
      const filename = selectedFile.name.toLocaleLowerCase();
      const isStandard =
        !allowAi || filename.endsWith('.gpx') || filename.endsWith('.kml');
      const result = await (isStandard ? analyzeStandard : analyze)({
        variables: isStandard
          ? { allowAi, file: selectedFile }
          : { file: selectedFile },
      });
      const imported = isStandard
        ? result.data?.analyzeStandardImport?.standardImport
        : result.data?.analyzeSmartImport?.smartImport;
      const id = imported?.id;

      if (id) {
        navigate(
          imported.importMode === 'standard'
            ? `/imports/standard/${id}/workflow`
            : `/imports/${id}/workflow`,
          { replace: true }
        );
      }
    } catch {
      setFileError(
        intl.formatMessage({
          id: 'imports.upload.error',
          defaultMessage: 'The file could not be uploaded.',
        })
      );
    }
  }, [allowAi, analyze, analyzeStandard, intl, navigate, selectedFile]);
  const showSmallBatchSuggestion =
    allowAi &&
    selectedFindCount != null &&
    selectedFindCount > 0 &&
    selectedFindCount < 20;
  const allowAiSwitchInputProps = useMemo(
    () => ({
      'aria-label': intl.formatMessage({
        id: 'imports.upload.allowAi',
        defaultMessage: 'Allow AI calls',
      }),
    }),
    [intl]
  );
  const headerControl = useMemo(
    () => (
      <Stack gap={0.5}>
        {showSmallBatchSuggestion && (
          <Stack direction="row" gap={0.75} sx={smallBatchSuggestionStyles}>
            <InfoOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              <FormattedMessage
                id="imports.upload.smallBatch"
                defaultMessage="There are fewer than 20 Finds in this batch. Disabling AI and setting the details manually might be a fitting option."
              />
            </Typography>
          </Stack>
        )}
        <SwitchLabel
          control={
            <Switch
              checked={allowAi}
              disabled={loading}
              inputProps={allowAiSwitchInputProps}
              onChange={handleAllowAiChange}
            />
          }
          label={intl.formatMessage({
            id: 'imports.upload.allowAi',
            defaultMessage: 'Allow AI calls',
          })}
        />
      </Stack>
    ),
    [
      allowAi,
      allowAiSwitchInputProps,
      handleAllowAiChange,
      intl,
      loading,
      showSmallBatchSuggestion,
    ]
  );
  const actions = useMemo(
    () => (
      <>
        <Button disabled={loading} onClick={onClose} variant="outlined">
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          disabled={loading || !selectedFile}
          onClick={handleAnalyze}
          variant="contained"
        >
          <FormattedMessage
            id="imports.upload.analyze"
            defaultMessage="Upload and analyze"
          />
        </Button>
      </>
    ),
    [handleAnalyze, loading, onClose, selectedFile]
  );

  return (
    <ImportWorkflowShell
      actions={actions}
      closeDisabled={loading}
      description={intl.formatMessage({
        id: 'imports.upload.description',
        defaultMessage:
          'Add GPX, KML, CSV, TSV, or XLSX. With AI enabled, useful archaeological names and descriptions can fill missing details. Turn it off for a fully local import.',
      })}
      headerControl={headerControl}
      onClose={onClose}
      progressLabel={
        loading
          ? intl.formatMessage({
              id: 'imports.upload.progress',
              defaultMessage: 'Uploading and validating…',
            })
          : undefined
      }
      step={intl.formatMessage({
        id: 'imports.upload.step',
        defaultMessage: 'Step 1: Upload find list',
      })}
    >
      <Stack sx={uploadMainStyles} gap={2}>
        {(fileError || error) && (
          <Alert severity="error">
            {fileError ||
              intl.formatMessage({
                id: 'imports.upload.error',
                defaultMessage: 'The file could not be uploaded.',
              })}
          </Alert>
        )}
        {selectedFile && !loading && (
          <Alert severity="info">
            <FormattedMessage
              id="imports.upload.selectedFile"
              defaultMessage="Selected file: {filename}"
              values={{ filename: selectedFile.name }}
            />
          </Alert>
        )}
        {loading ? (
          <Stack
            gap={1}
            aria-label={intl.formatMessage({
              id: 'imports.upload.parsingRows',
              defaultMessage: 'Parsing uploaded rows',
            })}
          >
            {skeletonRows.map((row) => (
              <Skeleton height={48} key={row} variant="rounded" />
            ))}
          </Stack>
        ) : (
          <ImportDropzone
            disabled={loading}
            onError={handleFileError}
            onFile={handleFile}
          />
        )}
      </Stack>
    </ImportWorkflowShell>
  );
});

interface ExistingImportProps {
  importId: string;
  onClose: () => void;
  standard: boolean;
}

const ExistingImport = React.memo(
  ({ importId, onClose, standard }: ExistingImportProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const initializedImport = useRef<string | null>(null);
    const loadedDraftKey = useRef<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [draftDirty, setDraftDirty] = useState(false);
    const [draftRowsHydrating, setDraftRowsHydrating] = useState(true);
    const [draftRows, setDraftRows] = useState<SmartImportDraftRow[]>([]);
    const [draftSessions, setDraftSessions] = useState<
      StandardImportDraftSession[]
    >([]);
    const [, startDraftRowsTransition] = useTransition();
    const [globalSite, setGlobalSite] = useState<SiteChoice | null>(null);
    const [rowOverrides, setRowOverrides] = useState<
      Record<string, string | null>
    >({});
    const {
      data,
      error,
      loading,
      refetch,
      startPolling,
      stopPolling,
    } = useQuery(standard ? STANDARD_IMPORT_QUERY : SMART_IMPORT_QUERY, {
      variables: { id: importId },
      fetchPolicy: 'cache-and-network',
    });
    const { data: sitesData } = useQuery(GET_MY_SITES_QUERY, {
      fetchPolicy: 'cache-first',
    });
    const { data: filterData } = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
      fetchPolicy: 'cache-first',
    });
    const [confirmImport, { loading: confirming }] = useMutation(
      CONFIRM_SMART_IMPORT_MUTATION
    );
    const [deleteImport, { loading: deleting }] = useMutation(
      standard ? DELETE_STANDARD_IMPORT_MUTATION : DELETE_SMART_IMPORT_MUTATION
    );
    const [updateDraft, { loading: saving }] = useMutation(
      standard
        ? UPDATE_STANDARD_IMPORT_DRAFT_MUTATION
        : UPDATE_SMART_IMPORT_DRAFT_MUTATION
    );
    const [commitImport, { loading: committing }] = useMutation(
      standard ? COMMIT_STANDARD_IMPORT_MUTATION : COMMIT_SMART_IMPORT_MUTATION,
      {
        update: (cache, { data: commitData }) => {
          const result = standard
            ? commitData?.commitStandardImport
            : commitData?.commitSmartImport;
          const createdFindIds = result?.createdFinds?.map(
            (createdFind) => createdFind.id
          );

          if (createdFindIds?.length) {
            reconcileFindsInCache(cache, createdFindIds);
          }
        },
      }
    );
    const smartImport = (standard
      ? data?.standardImport
      : data?.smartImport) as SmartImportRecord | null | undefined;
    const smartImportStatus = smartImport?.status;
    const draftHydrationKey = smartImport?.draft
      ? `${smartImport.id}:${smartImport.status}:${smartImport.draft.revision}`
      : null;
    const sites = useMemo<SiteOption[]>(() => sitesData?.getMySites || [], [
      sitesData?.getMySites,
    ]);
    const eraOptions = useMemo<string[]>(
      () =>
        (filterData?.getAllFilterTypes?.eras || [])
          .map((era) => era.id.toLocaleLowerCase())
          .sort(),
      [filterData?.getAllFilterTypes?.eras]
    );
    const findTypeOptions = useMemo<string[]>(
      () =>
        (filterData?.getAllFilterTypes?.findTypes || [])
          .map((type) => type.id)
          .sort(),
      [filterData?.getAllFilterTypes?.findTypes]
    );
    const materialOptions = useMemo<string[]>(
      () => [...(filterData?.getAllFilterTypes?.materials || [])].sort(),
      [filterData?.getAllFilterTypes?.materials]
    );
    const tagOptions = useMemo<string[]>(
      () =>
        (filterData?.getAllFilterTypes?.tags || [])
          .map((tag) => tag.name)
          .sort(),
      [filterData?.getAllFilterTypes?.tags]
    );

    useEffect(() => {
      if (!smartImportStatus) {
        return;
      }

      if (['processing', 'importing'].includes(smartImportStatus)) {
        startPolling(2000);
      } else {
        stopPolling();
      }

      return stopPolling;
    }, [smartImportStatus, startPolling, stopPolling]);

    useEffect(() => {
      if (!smartImport || initializedImport.current === smartImport.id) {
        return;
      }

      initializedImport.current = smartImport.id;
      const persistedRows = Object.fromEntries(
        smartImport.overrides.rows.map((row) => [
          row.rowRef,
          row.overrides.siteId,
        ])
      );

      setRowOverrides(persistedRows);

      if (smartImport.status !== 'awaiting_confirmation') {
        setGlobalSite(
          getSiteChoice(smartImport.overrides.global.siteId, sites)
        );
      }
    }, [sites, smartImport]);

    useEffect(() => {
      if (!globalSite?.id) {
        return;
      }

      const loadedSite = sites.find((site) => site.id === globalSite.id);

      if (loadedSite && loadedSite.name !== globalSite.name) {
        setGlobalSite(loadedSite);
      }
    }, [globalSite, sites]);

    useEffect(() => {
      const revision = smartImport?.draft?.revision;

      if (
        revision == null ||
        loadedDraftKey.current === draftHydrationKey ||
        draftDirty
      ) {
        return;
      }

      loadedDraftKey.current = draftHydrationKey;
      const rows = smartImport?.draft?.rows || [];

      setDraftSessions(smartImport?.draft?.sessions || []);

      if (draftRows.length > 0) {
        setDraftRows(rows);
        return;
      }

      setDraftRowsHydrating(true);
      startDraftRowsTransition(() => {
        setDraftRows(rows);
        setDraftRowsHydrating(false);
      });
    }, [draftDirty, draftHydrationKey, draftRows.length, smartImport?.draft]);

    const handleGlobalSiteChange = useCallback((choice: SiteChoice | null) => {
      setGlobalSite(choice);
    }, []);
    const handleRowSiteChange = useCallback(
      (rowRef: string, choice: SiteChoice | null) => {
        setRowOverrides((current) => {
          const next = { ...current };

          if (choice == null) {
            delete next[rowRef];
          } else {
            next[rowRef] = choice.id;
          }

          return next;
        });
      },
      []
    );
    const handleFindChange = useCallback(
      (
        rowRef: string,
        field: keyof SmartImportDraftFind,
        value: SmartImportDraftFind[keyof SmartImportDraftFind]
      ) => {
        setDraftRows((current) =>
          current.map((row) =>
            row.rowRef === rowRef
              ? { ...row, find: { ...row.find, [field]: value } }
              : row
          )
        );
        setDraftDirty(true);
      },
      []
    );
    const handleIncludeChange = useCallback(
      (rowRef: string, include: boolean) => {
        setDraftRows((current) =>
          current.map((row) =>
            row.rowRef === rowRef ? { ...row, include } : row
          )
        );
        setDraftDirty(true);
      },
      []
    );
    const handleSessionIncludeChange = useCallback(
      (sessionRef: string, include: boolean) => {
        setDraftSessions((current) =>
          current.map((session) =>
            session.sessionRef === sessionRef
              ? { ...session, include }
              : session
          )
        );
        setDraftDirty(true);
      },
      []
    );
    const handleApplyGlobalSite = useCallback(() => {
      if (!globalSite) {
        return;
      }

      setDraftRows((current) =>
        current.map((row) => ({
          ...row,
          find: { ...row.find, siteId: globalSite.id },
        }))
      );
      setDraftDirty(true);
    }, [globalSite]);

    const buildOverrides = useCallback(() => {
      if (!globalSite) {
        throw new Error(
          intl.formatMessage({
            id: 'imports.validation.selectSite',
            defaultMessage: 'Select a site or explicitly select None.',
          })
        );
      }

      return {
        global: { siteId: globalSite.id },
        rows: Object.entries(rowOverrides).map(([rowRef, siteId]) => ({
          rowRef,
          overrides: { siteId },
        })),
      };
    }, [globalSite, intl, rowOverrides]);

    const handleConfirm = useCallback(async () => {
      setActionError(null);

      try {
        await confirmImport({
          variables: { id: importId, overrides: buildOverrides() },
        });
        await refetch();
      } catch {
        setActionError(
          intl.formatMessage({
            id: 'imports.error.confirm',
            defaultMessage: 'The import could not be confirmed.',
          })
        );
      }
    }, [buildOverrides, confirmImport, importId, intl, refetch]);

    const handleDelete = useCallback(async () => {
      setActionError(null);

      try {
        await deleteImport({ variables: { id: importId } });
        navigate('/imports');
      } catch {
        setActionError(
          intl.formatMessage({
            id: 'imports.error.cancel',
            defaultMessage: 'The import could not be cancelled.',
          })
        );
      }
    }, [deleteImport, importId, intl, navigate]);

    const handleDifferentFile = useCallback(async () => {
      setActionError(null);

      try {
        await deleteImport({ variables: { id: importId } });
        navigate('/imports/create', { replace: true });
      } catch {
        setActionError(
          intl.formatMessage({
            id: 'imports.error.removeUpload',
            defaultMessage: 'The current upload could not be removed.',
          })
        );
      }
    }, [deleteImport, importId, intl, navigate]);

    const saveDraft = useCallback(async () => {
      await updateDraft({
        variables: {
          id: importId,
          draft: buildDraftInput(draftRows, draftSessions),
        },
      });
      setDraftDirty(false);
      await refetch();
    }, [draftRows, draftSessions, importId, refetch, updateDraft]);

    const handleSave = useCallback(async () => {
      setActionError(null);

      try {
        await saveDraft();
      } catch {
        setActionError(
          intl.formatMessage({
            id: 'imports.error.saveDraft',
            defaultMessage: 'The draft could not be saved.',
          })
        );
      }
    }, [intl, saveDraft]);

    const handleCommit = useCallback(async () => {
      setActionError(null);

      try {
        if (draftDirty) {
          await saveDraft();
        }

        await commitImport({ variables: { id: importId } });
        await refetch();
      } catch {
        setActionError(
          intl.formatMessage({
            id: 'imports.error.store',
            defaultMessage: 'The Finds could not be stored.',
          })
        );
      }
    }, [commitImport, draftDirty, importId, intl, refetch, saveDraft]);

    if (loading && !smartImport) {
      const actions = (
        <Button onClick={onClose}>
          <FormattedMessage id="general.close" defaultMessage="Close" />
        </Button>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.loading.description',
            defaultMessage: 'Loading the persisted import state.',
          })}
          onClose={onClose}
          progressLabel={intl.formatMessage({
            id: 'general.loading',
            defaultMessage: 'Loading…',
          })}
          step={intl.formatMessage({
            id: 'imports.workflow.title',
            defaultMessage: 'Import finds',
          })}
        >
          <DraftRowsSkeleton />
        </ImportWorkflowShell>
      );
    }

    if (error || !smartImport) {
      const actions = (
        <Button onClick={onClose}>
          <FormattedMessage id="general.close" defaultMessage="Close" />
        </Button>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.detail.error.load',
            defaultMessage: 'Import could not be loaded.',
          })}
          onClose={onClose}
          step={intl.formatMessage({
            id: 'imports.unavailable',
            defaultMessage: 'Import unavailable',
          })}
        >
          <Alert severity="error">
            <FormattedMessage
              id="imports.detail.error.load"
              defaultMessage="Import could not be loaded."
            />
          </Alert>
        </ImportWorkflowShell>
      );
    }

    const busy = confirming || deleting || saving || committing;
    const draftRowsLoading =
      draftRowsHydrating ||
      (draftRows.length === 0 &&
        (smartImport.draft == null ||
          loadedDraftKey.current !== draftHydrationKey));
    const cancelButton = isImportCancellable(smartImport.status) ? (
      <Button
        color="error"
        disabled={busy}
        onClick={handleDelete}
        startIcon={<DeleteOutlineIcon />}
        variant="outlined"
      >
        <FormattedMessage id="imports.cancel" defaultMessage="Cancel import" />
      </Button>
    ) : null;

    if (smartImport.status === 'awaiting_confirmation') {
      const actions = (
        <>
          {cancelButton}
          <Button
            disabled={busy}
            onClick={handleDifferentFile}
            startIcon={<UploadFileOutlinedIcon />}
            variant="outlined"
          >
            <FormattedMessage
              id="imports.validation.differentFile"
              defaultMessage="Upload different file"
            />
          </Button>
          <Button
            disabled={busy || !globalSite}
            onClick={handleConfirm}
            variant="contained"
          >
            <FormattedMessage
              id="imports.validation.approve"
              defaultMessage="Approve and process"
            />
          </Button>
        </>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.validation.description',
            defaultMessage:
              'Review the detected columns and sample rows. Choose the default site before approved archaeological text and recognized spreadsheet group headers are sent for interpretation.',
          })}
          onClose={onClose}
          step={intl.formatMessage({
            id: 'imports.validation.step',
            defaultMessage: 'Step 2: Validation',
          })}
        >
          <Stack gap={2} flexGrow={1} minHeight={0}>
            {(actionError || smartImport.analysis.warnings.length > 0) && (
              <Alert severity={actionError ? 'error' : 'info'}>
                {actionError ||
                  smartImport.analysis.warnings
                    .map((warning) => formatImportWarning(warning, intl))
                    .join(' ')}
              </Alert>
            )}
            <Box sx={validationControlsStyles}>
              <Box sx={globalSiteStyles}>
                <SiteSelect
                  allowUnselected
                  label={intl.formatMessage({
                    id: 'imports.validation.defaultSite',
                    defaultMessage: 'Default site (required)',
                  })}
                  onChange={handleGlobalSiteChange}
                  placeholder={intl.formatMessage({
                    id: 'imports.validation.sitePlaceholder',
                    defaultMessage: 'Select a site or None',
                  })}
                  sites={sites}
                  value={globalSite}
                />
              </Box>
              <Typography color="text.secondary" variant="body2">
                <FormattedMessage
                  id="imports.validation.overrideHelp"
                  defaultMessage="Individual preview rows can override this choice. More rows can be changed during final review."
                />
              </Typography>
            </Box>
            {globalSite ? (
              <ImportPreviewTable
                analysis={smartImport.analysis}
                globalSite={globalSite}
                onRowSiteChange={handleRowSiteChange}
                rowOverrides={rowOverrides}
                sites={sites}
              />
            ) : (
              <Alert severity="warning">
                <FormattedMessage
                  id="imports.validation.selectSiteToContinue"
                  defaultMessage="Select a site or explicitly select None to continue."
                />
              </Alert>
            )}
          </Stack>
        </ImportWorkflowShell>
      );
    }

    if (smartImport.status === 'processing') {
      const actions = cancelButton || (
        <Button onClick={onClose}>
          <FormattedMessage id="general.close" defaultMessage="Close" />
        </Button>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.interpretation.description',
            defaultMessage:
              'Acolyte is preparing a semantic profile and interpreting only the approved archaeological fields. You can close this screen and return later.',
          })}
          onClose={onClose}
          progressLabel={intl.formatMessage({
            id: 'imports.status.processing',
            defaultMessage: 'Processing',
          })}
          step={intl.formatMessage({
            id: 'imports.interpretation.step',
            defaultMessage: 'Step 3: Interpretation',
          })}
        >
          <ImportProcessing smartImport={smartImport} />
        </ImportWorkflowShell>
      );
    }

    if (smartImport.status === 'failed') {
      const actions = (
        <>
          {cancelButton}
          <Button
            disabled={busy || !globalSite}
            onClick={handleConfirm}
            variant="contained"
          >
            <FormattedMessage
              id="imports.interpretation.retry"
              defaultMessage="Retry processing"
            />
          </Button>
        </>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.interpretation.failedDescription',
            defaultMessage:
              'Processing stopped before a complete draft was produced. The persisted source and completed work can be retried.',
          })}
          onClose={onClose}
          step={intl.formatMessage({
            id: 'imports.interpretation.failedStep',
            defaultMessage: 'Step 3: Interpretation failed',
          })}
        >
          <Paper variant="outlined" sx={errorPaperStyles}>
            <Alert severity="error">
              {actionError || formatImportFailure(smartImport.error, intl)}
            </Alert>
          </Paper>
        </ImportWorkflowShell>
      );
    }

    if (smartImport.status === 'ready_for_review') {
      const hasIncludedItems =
        draftRows.some((row) => row.include) ||
        draftSessions.some((session) => session.include);
      const actions = (
        <>
          {cancelButton}
          <Button
            disabled={busy || !draftDirty}
            onClick={handleSave}
            startIcon={<SaveOutlinedIcon />}
            variant="outlined"
          >
            <FormattedMessage
              id="imports.review.save"
              defaultMessage="Save review progress"
            />
          </Button>
          <Button
            disabled={busy || !hasIncludedItems}
            onClick={handleCommit}
            variant="contained"
          >
            {committing ? (
              <FormattedMessage
                id="imports.status.storingProgress"
                defaultMessage="Storing…"
              />
            ) : (
              <FormattedMessage
                id="imports.review.confirm"
                defaultMessage="Confirm and store"
              />
            )}
          </Button>
        </>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={
            standard
              ? intl.formatMessage({
                  id: 'imports.review.standardDescription',
                  defaultMessage:
                    'Review the imported points and field sessions. Saving preserves this import workspace only; Finds are created after Confirm and store.',
                })
              : intl.formatMessage({
                  id: 'imports.review.description',
                  defaultMessage:
                    'Review every Find before storage. Saving preserves this import workspace only; Finds are created after Confirm and store.',
                })
          }
          onClose={onClose}
          progressLabel={
            committing
              ? intl.formatMessage({
                  id: 'imports.store.progress',
                  defaultMessage: 'Storing Finds…',
                })
              : saving
              ? intl.formatMessage({
                  id: 'imports.review.saving',
                  defaultMessage: 'Saving draft…',
                })
              : undefined
          }
          step={
            standard
              ? intl.formatMessage({
                  id: 'imports.review.standardStep',
                  defaultMessage: 'Review standard import',
                })
              : intl.formatMessage({
                  id: 'imports.review.step',
                  defaultMessage: 'Step 4: Review Finds',
                })
          }
        >
          <Stack gap={2} flexGrow={1} minHeight={0}>
            {actionError && <Alert severity="error">{actionError}</Alert>}
            <Alert severity="info">
              <FormattedMessage
                id="imports.review.saveHelp"
                defaultMessage="Save review progress stores your edits to this import. It does not create Finds or control whether the final Finds are marked as drafts."
              />
            </Alert>
            {draftSessions.length > 0 && (
              <Stack gap={1}>
                <Typography variant="h6">
                  <FormattedMessage
                    id="fieldSessions.title"
                    defaultMessage="Field sessions"
                  />
                </Typography>
                {draftSessions.map((session) => (
                  <SessionRow
                    disabled={busy}
                    key={session.sessionRef}
                    onIncludeChange={handleSessionIncludeChange}
                    session={session}
                  />
                ))}
              </Stack>
            )}
            {standard && draftRows.length > 0 && (
              <Typography variant="h6">
                <FormattedMessage
                  id="imports.review.findWaypoints"
                  defaultMessage="Find waypoints"
                />
              </Typography>
            )}
            {draftRows.length > 0 && (
              <>
                <Box sx={reviewToolbarStyles}>
                  <Box sx={reviewSiteStyles}>
                    <SiteSelect
                      label={intl.formatMessage({
                        id: 'imports.review.globalSite',
                        defaultMessage: 'Site to apply to all rows',
                      })}
                      onChange={handleGlobalSiteChange}
                      sites={sites}
                      value={globalSite || NONE_SITE_CHOICE}
                    />
                  </Box>
                  <Button onClick={handleApplyGlobalSite} variant="outlined">
                    <FormattedMessage
                      id="imports.review.applySite"
                      defaultMessage="Apply site to all rows"
                    />
                  </Button>
                  <Typography color="text.secondary" variant="body2">
                    <FormattedMessage
                      id="imports.review.siteOverrideHelp"
                      defaultMessage="Individual Site cells can still override this value."
                    />
                  </Typography>
                </Box>
                {draftRowsLoading ? (
                  <DraftRowsSkeleton />
                ) : (
                  <ImportReviewTable
                    disabled={busy}
                    eraOptions={eraOptions}
                    findTypeOptions={findTypeOptions}
                    materialOptions={materialOptions}
                    onFindChange={handleFindChange}
                    onIncludeChange={handleIncludeChange}
                    rows={draftRows}
                    sites={sites}
                    tagOptions={tagOptions}
                  />
                )}
              </>
            )}
          </Stack>
        </ImportWorkflowShell>
      );
    }

    if (smartImport.status === 'importing') {
      const actions = (
        <Button disabled>
          <FormattedMessage
            id="imports.status.storingProgress"
            defaultMessage="Storing…"
          />
        </Button>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage({
            id: 'imports.store.description',
            defaultMessage:
              'The reviewed draft is being stored transactionally. Editing and navigation actions are temporarily disabled.',
          })}
          onClose={onClose}
          progressLabel={intl.formatMessage({
            id: 'imports.store.progress',
            defaultMessage: 'Storing Finds…',
          })}
          step={intl.formatMessage({
            id: 'imports.store.step',
            defaultMessage: 'Step 5: Store Finds',
          })}
        >
          <Paper sx={centeredPaperStyles}>
            <Typography variant="h6">
              <FormattedMessage
                id="imports.store.heading"
                defaultMessage="Storing Finds"
              />
            </Typography>
            <LinearProgress sx={storingProgressStyles} />
          </Paper>
        </ImportWorkflowShell>
      );
    }

    if (smartImport.status === 'completed') {
      const globalSiteId = smartImport.overrides.global.siteId;
      const completedRows = draftRows.filter((row) => row.include);
      const completedStepIcon = (
        <CheckCircleOutlineIcon sx={successStepIconStyles} />
      );
      const actions = (
        <>
          <Button onClick={onClose} variant="outlined">
            <FormattedMessage id="general.close" defaultMessage="Close" />
          </Button>
          {globalSiteId && (
            <Button
              component={Link}
              to={`/s/${globalSiteId}/finds`}
              variant="contained"
            >
              <FormattedMessage
                id="imports.complete.goToFinds"
                defaultMessage="Go to site Finds"
              />
            </Button>
          )}
        </>
      );

      return (
        <ImportWorkflowShell
          actions={actions}
          description={intl.formatMessage(
            {
              id: 'imports.complete.description',
              defaultMessage:
                'Successfully imported {findCount, plural, one {# Find} other {# Finds}} and {sessionCount, plural, one {# field session} other {# field sessions}}. The items below are now stored and cannot be edited here.',
            },
            {
              findCount: smartImport.committedFindIds.length,
              sessionCount: smartImport.committedFieldSessionIds.length,
            }
          )}
          onClose={onClose}
          step={intl.formatMessage({
            id: 'imports.complete.step',
            defaultMessage: 'Import complete',
          })}
          stepIcon={completedStepIcon}
        >
          <Stack gap={2} flexGrow={1} minHeight={0}>
            {draftSessions.length > 0 && (
              <Stack gap={1}>
                <Typography variant="h6">
                  <FormattedMessage
                    id="fieldSessions.title"
                    defaultMessage="Field sessions"
                  />
                </Typography>
                {draftSessions
                  .filter((session) => session.include)
                  .map((session) => (
                    <SessionRow
                      disabled
                      key={session.sessionRef}
                      onIncludeChange={handleSessionIncludeChange}
                      session={session}
                    />
                  ))}
              </Stack>
            )}
            {draftRowsLoading ? (
              <DraftRowsSkeleton />
            ) : (
              <ImportReviewTable
                committedFindIds={smartImport.committedFindIds}
                disabled
                eraOptions={eraOptions}
                findTypeOptions={findTypeOptions}
                materialOptions={materialOptions}
                onFindChange={handleFindChange}
                onIncludeChange={handleIncludeChange}
                rows={completedRows}
                sites={sites}
                tagOptions={tagOptions}
              />
            )}
          </Stack>
        </ImportWorkflowShell>
      );
    }

    const actions = (
      <Button onClick={onClose}>
        <FormattedMessage id="general.close" defaultMessage="Close" />
      </Button>
    );

    return (
      <ImportWorkflowShell
        actions={actions}
        description={intl.formatMessage({
          id: 'imports.cancelled.description',
          defaultMessage: 'This import is no longer active.',
        })}
        onClose={onClose}
        step={intl.formatMessage({
          id: 'imports.cancelled.step',
          defaultMessage: 'Import cancelled',
        })}
      >
        <Alert severity="info">
          <FormattedMessage
            id="imports.cancelled.message"
            defaultMessage="The import was cancelled."
          />
        </Alert>
      </ImportWorkflowShell>
    );
  }
);

export const ImportWorkflow = React.memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { importId } = useParams();
  const standard = location.pathname.includes('/imports/standard/');
  const handleCloseUpload = useCallback(() => navigate('/imports'), [navigate]);
  const handleCloseImport = useCallback(
    () => navigate(importId && !standard ? `/imports/${importId}` : '/imports'),
    [importId, navigate, standard]
  );

  return importId ? (
    <ExistingImport
      importId={importId}
      onClose={handleCloseImport}
      standard={standard}
    />
  ) : (
    <UploadImport onClose={handleCloseUpload} />
  );
});
